import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { Bevel } from './Bevel';
import { TitleBar } from './TitleBar';
import { makeStyles } from './ThemeContext';

export interface WindowProps {
  title: string;
  active?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  icon?: React.ReactNode;
  /** Rendered between the title bar and the content, e.g. a MenuBar. */
  menu?: React.ReactNode;
  /** Rendered as a sunken well along the bottom edge. */
  statusBar?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}

/** A complete Win95 window: raised frame, caption, optional menu and status bar. */
export function Window({
  title,
  active = true,
  onClose,
  onMinimize,
  onMaximize,
  icon,
  menu,
  statusBar,
  style,
  contentStyle,
  children,
}: WindowProps) {
  const styles = useStyles();
  return (
    <Bevel variant="raised" style={[styles.frame, style]} contentStyle={styles.frameInner}>
      <TitleBar
        title={title}
        active={active}
        onClose={onClose}
        onMinimize={onMinimize}
        onMaximize={onMaximize}
        icon={icon}
      />
      {menu}
      <View style={[styles.content, contentStyle]}>{children}</View>
      {statusBar ? (
        <Bevel variant="sunken" style={styles.status} contentStyle={styles.statusInner}>
          {statusBar}
        </Bevel>
      ) : null}
    </Bevel>
  );
}

const useStyles = makeStyles((t) => ({
  frame: { flex: 1 },
  frameInner: { padding: t.metrics.windowPadding, backgroundColor: t.colors.face },
  content: { flex: 1, marginTop: t.metrics.windowPadding },
  status: { marginTop: t.metrics.windowPadding, height: 20 },
  statusInner: { justifyContent: 'center' as const, paddingHorizontal: 4 },
}));
